import React from 'react'
import styled from 'styled-components'
import Blogik, { BlogConsumer, BlogButton } from 'components/shared/Blogik'

// Components
import Image from 'components/shared/ImageGatsby'

// Elements
import ButtonDefault from 'components/elements/ButtonDefault'
import { ScrollRevealItem } from 'components/shared/ScrollReveal'

import { Link } from 'gatsby'

/* Matches ProjectSlider slide card layout */
const ProjectCardImage = styled(Image)`
  position: absolute !important;
  top: 0;
  left: 0;
  width: 100% !important;
  height: 100% !important;

  & .gatsby-image-wrapper,
  & picture,
  & img {
    width: 100% !important;
    height: 100% !important;
  }

  & img {
    object-fit: cover;
  }
`

const SlidePlaceholder = styled.div`
  position: absolute;
  inset: 0;
  background-color: ${(props) => props.theme.color.face.mainDark};
`

const SlideGradient = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 40%;
  background: linear-gradient(
    to top,
    rgba(55, 18, 22, 0.85) 0%,
    rgba(55, 18, 22, 0.35) 45%,
    rgba(0, 0, 0, 0) 100%
  );
  pointer-events: none;
  z-index: 1;
`

const SlideCaption = styled.span`
  position: absolute;
  bottom: 52px;
  left: 0;
  right: 0;
  text-align: center;
  font-family: ${(props) => props.theme.font.family.secondary};
  font-size: ${(props) => props.theme.font.size['20']};
  font-weight: 300;
  color: ${(props) => props.theme.color.text.light};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  z-index: 2;
  pointer-events: none;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
  transition: bottom 0.28s ease;

  @media (max-width: 767px) {
    font-size: ${(props) => props.theme.font.size['18']};
  }
`

const SlideLink = styled(Link)`
  display: block;
  position: relative;
  overflow: hidden;
  width: 100%;
  aspect-ratio: 3 / 4;
  min-height: 280px;
  text-decoration: none;
  color: inherit;

  &:hover,
  &:focus-visible {
    text-decoration: none;
    color: inherit;
  }

  &:hover ${SlideCaption}, &:focus-visible ${SlideCaption} {
    bottom: 76px;
  }

  &:hover .slide-hover-extras,
  &:focus-visible .slide-hover-extras {
    opacity: 1;
  }
`

const SlideHoverExtras = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.28s ease;
`

const SlideBadge = styled.span`
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 0.4rem 0.9rem;
  border-radius: 999px;
  background-color: ${(props) => props.theme.color.face.mainLight};
  color: ${(props) => props.theme.color.face.contrast};
  font-family: ${(props) => props.theme.font.family.secondary};
  font-size: ${(props) => props.theme.font.size['14']};
  font-weight: 600;
  line-height: 1.2;
`

const SlideCta = styled.span`
  position: absolute;
  bottom: 26px;
  left: 50%;
  transform: translateX(-50%);
  display: inline-block;
  padding: 0.45rem 1.15rem;
  white-space: nowrap;
  border: 1px solid ${(props) => props.theme.color.text.light};
  border-radius: 4px;
  font-family: ${(props) => props.theme.font.family.secondary};
  font-size: ${(props) => props.theme.font.size['14']};
  font-weight: 600;
  color: ${(props) => props.theme.color.text.light};
  text-transform: uppercase;
  letter-spacing: 0.08em;
`

const DEFAULT_CTA = 'BEKIJK PROJECT →'

const NoCategoryMessage = styled.h3`
  font-size: ${(props) => props.theme.font.size[21]};
  font-weight: ${(props) => props.theme.font.weight.s};
`

interface PostsProjectsProps {
  // eslint-disable-next-line
  fields: GatsbyTypes.WpPage_Flexcontent_Flex_Posts
}

function PostsProjects({ fields }: PostsProjectsProps) {
  return (
    <section className="py-5">
      <div className="container">
        <div className="row justify-content-center">
          <div>
            <Blogik
              postType="projects"
              settings={{
                id: 'blog',
                limit: 6,
              }}
            >
              {fields ? (
                <div>
                  <BlogGrid projectFields={fields} />
                </div>
              ) : (
                <h4>Geen projecten gevonden, kijk later nog eens.</h4>
              )}
            </Blogik>
          </div>
        </div>
      </div>
    </section>
  )
}

interface BlogGridProps {
  // eslint-disable-next-line
  projectFields: GatsbyTypes.WpPage_Flexcontent_Flex_Posts
}

function BlogGrid({ projectFields }: BlogGridProps) {
  return (
    <BlogConsumer>
      {({ posts, showMoreButton, hasPosts }: any) => {
        if (hasPosts) {
          return (
            <>
              <div className="d-flex flex-wrap">
                {posts.map((post: any, index: number) => (
                  <ScrollRevealItem
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    className="col-12 col-md-4 mb-4"
                    sequenceIndex={index}
                  >
                    <Project fields={post} projectFields={projectFields} />
                  </ScrollRevealItem>
                ))}
              </div>
              {showMoreButton && (
                <div className="mb-5 mt-5 mt-md-0 text-center">
                  <ButtonDefault to="/" isCustom>
                    <BlogButton>laad meer projecten</BlogButton>
                  </ButtonDefault>
                </div>
              )}
            </>
          )
        }
        return (
          <NoCategoryMessage className="text-center pb-5">
            Geen projecten gevonden in deze categorie
          </NoCategoryMessage>
        )
      }}
    </BlogConsumer>
  )
}

interface ProjectProps {
  fields: any
  // eslint-disable-next-line
  projectFields: GatsbyTypes.WpPage_Flexcontent_Flex_Posts
}

function Project({ fields, projectFields }: ProjectProps) {
  const { node } = fields
  const thumbnail = node.projectDetail?.thumbnail
  const categoryName = node.projectCategories?.nodes?.[0]?.name || null
  const ctaLabel = projectFields?.readMoreText?.trim() || DEFAULT_CTA

  return (
    <SlideLink to={node.uri || '/'}>
      {thumbnail ? (
        <ProjectCardImage
          image={thumbnail}
          alt={node.title || ''}
          loading="lazy"
        />
      ) : (
        <SlidePlaceholder aria-hidden />
      )}
      <SlideGradient aria-hidden />
      <SlideCaption>{node.title}</SlideCaption>
      <SlideHoverExtras className="slide-hover-extras">
        {categoryName && <SlideBadge>{categoryName}</SlideBadge>}
        <SlideCta>{ctaLabel}</SlideCta>
      </SlideHoverExtras>
    </SlideLink>
  )
}

export default PostsProjects
